/**
 * InsightPro™ — Balance Sheet  (Multi-Entity Finance clients only)
 *
 * KPI strip (Assets · Liab · Equity · Working Capital · Current Ratio · Debt:Equity)
 * + two-column tree layout (Assets | Equity & Liabilities), each grouped by
 * report_category and expandable to GL-account level. Clicking any row opens
 * a per-entity panel showing which entities contribute to that section / GL.
 *
 * Data: getBalanceSheet → { sections[], accounts[], totals }
 *       getBalanceSheetEntities → per-entity drill (filterable by section + GL)
 * BS data is parsed from Sage Evolution PDFs; reconciliation footer surfaces
 * any A − (L + E) imbalance so users know when totals can be trusted.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchFinancePeriods, fetchFinanceBrands,
  fetchBalanceSheet, fetchBSEntities,
} from '../utils/api';

// ── Number formatting (mirrors GroupPL.jsx) ──────────────────
const fmtMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return (v / 1_000_000).toFixed(2) + 'M';
  if (abs >= 1_000)     return (v / 1_000).toFixed(0) + 'K';
  return v.toFixed(0);
};
const fmtFull = (v) => (v == null || isNaN(v)) ? '—' :
  v.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtRatio = (v) => (v == null || isNaN(v)) ? '—' : v.toFixed(2);

// ── Styles (mirrors GroupPL/StorePerformance) ────────────────
const S = {
  page:        { padding: '20px 28px', maxWidth: 1500 },
  header:      { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 },
  title:       { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--page-text)', letterSpacing: '-0.02em' },
  sub:         { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginTop: 4, letterSpacing: '0.1em', textTransform: 'uppercase' },
  selectorBar: { display: 'flex', gap: 12, alignItems: 'flex-end' },
  label:       { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 5 },
  select:      { background: 'var(--page)', border: '1px solid var(--card-border)', padding: '8px 12px', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--page-text)', outline: 'none', borderRadius: 2, minWidth: 140 },
  kpiGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 },
  kpiCard:     { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '16px 20px' },
  kpiLabel:    { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 8 },
  kpiVal:      { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--page-text)', lineHeight: 1 },
  kpiSub:      { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', marginTop: 6 },
  twoCol:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  card:        { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '16px 20px' },
  cardTitle:   { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 12 },
  cardTotal:   { fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, color: 'var(--gold)', float: 'right' },
  sectionRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 8px', cursor: 'pointer', borderRadius: 2, fontSize: 13, color: 'var(--page-text)', borderBottom: '1px solid var(--card-border)' },
  sectionRowH: { background: 'rgba(200,149,32,0.04)' },
  sectionLbl:  { fontWeight: 600 },
  sectionMeta: { fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginLeft: 8 },
  sectionAmt:  { fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600 },
  glRow:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px 6px 28px', cursor: 'pointer', fontSize: 12, color: 'var(--page-muted)', borderBottom: '1px solid var(--card-border)' },
  glLbl:       { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, overflow: 'hidden' },
  glAcct:      { fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--page-muted)', letterSpacing: '0.06em' },
  glDesc:      { fontSize: 12, color: 'var(--page-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  glAmt:       { fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--page-text)', flexShrink: 0, marginLeft: 8 },
  caret:       { display: 'inline-block', width: 12, color: 'var(--page-muted)', fontFamily: 'var(--font-mono)', fontSize: 10, marginRight: 6 },
  errorBox:    { padding: 14, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--red)', background: 'rgba(176,40,40,0.08)', border: '1px solid rgba(176,40,40,0.2)', borderRadius: 2, marginBottom: 16 },
  loadingBox:  { padding: 28, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' },
  closeBtn:    { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'transparent', border: '1px solid var(--card-border)', padding: '4px 10px', cursor: 'pointer', color: 'var(--page-muted)', borderRadius: 2 },
  footNote:    { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', padding: '14px 4px 0', borderTop: '1px solid var(--card-border)', marginTop: 16, lineHeight: 1.55 },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--page-muted)', padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--card-border)', background: 'var(--page)' },
  thRight:     { textAlign: 'right' },
  td:          { padding: '9px 10px', fontSize: 12.5, color: 'var(--page-text)', borderBottom: '1px solid var(--card-border)', verticalAlign: 'middle' },
  tdRight:     { textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 },
  tdMono:      { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' },
};

// Current-ratio RAG: <1 red · 1–1.5 amber · ≥1.5 green
const ratioColor = (r) => {
  if (r == null || isNaN(r)) return 'var(--page-muted)';
  if (r < 1)   return 'var(--red)';
  if (r < 1.5) return 'var(--amber)';
  return 'var(--green-l)';
};

// ── Tree column ──────────────────────────────────────────────
function TreeColumn({ title, totalLabel, totalValue, sections, accounts, expanded, onToggle, onDrill }) {
  return (
    <div style={S.card}>
      <div style={S.cardTitle}>
        {title}
        <span style={S.cardTotal}>{totalLabel}: {fmtMoney(totalValue)}</span>
      </div>
      {sections.length === 0 ? (
        <div style={S.loadingBox}>No data.</div>
      ) : sections.map(sec => {
        const key = `${sec.accountType}::${sec.reportCategory}`;
        const isOpen = expanded.has(key);
        const glLines = accounts.filter(a =>
          a.accountType === sec.accountType && a.reportCategory === sec.reportCategory
        );
        return (
          <div key={key}>
            <div
              style={{ ...S.sectionRow, ...(isOpen ? S.sectionRowH : {}) }}
              onClick={() => onToggle(key)}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(200,149,32,0.06)'}
              onMouseLeave={e => e.currentTarget.style.background = isOpen ? 'rgba(200,149,32,0.04)' : ''}
            >
              <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                <span style={S.caret}>{isOpen ? '▾' : '▸'}</span>
                <span style={S.sectionLbl}>{sec.reportCategory || '(uncategorised)'}</span>
                <span style={S.sectionMeta}>{sec.accountCount} acct · {sec.entityCount} ent</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={S.sectionAmt}>{fmtMoney(sec.total)}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); onDrill({ section: sec }); }}
                  style={{ ...S.closeBtn, padding: '3px 8px' }}
                  title="See per-entity breakdown"
                >Entities</button>
              </div>
            </div>
            {isOpen && glLines.map(gl => (
              <div
                key={gl.glAccountKey}
                style={S.glRow}
                onClick={() => onDrill({ section: sec, account: gl })}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <div style={S.glLbl}>
                  <span style={S.glAcct}>{gl.glAccount}</span>
                  <span style={S.glDesc}>{gl.glDescription}</span>
                </div>
                <span style={S.glAmt}>{fmtMoney(gl.total)}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default function BalanceSheet() {
  const [periods, setPeriods]  = useState([]);
  const [period,  setPeriod]   = useState(null);
  const [brands,  setBrands]   = useState({ brands: ['Consolidated'], canPickBrand: false });
  const [brand,   setBrand]    = useState('Consolidated');
  const [bs,      setBs]       = useState(null);
  const [loading, setLoading]  = useState(true);
  const [err,     setErr]      = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [filters, setFilters]   = useState({});

  // Drill panel state — { section, account?, data? }
  const [drill, setDrill] = useState(null);

  // Periods + brands (one-shot)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, b] = await Promise.all([fetchFinancePeriods(), fetchFinanceBrands()]);
        if (cancelled) return;
        setPeriods(p.periods || []);
        setPeriod(p.latest || (p.periods?.[0]?.periodKey ?? null));
        setBrands(b);
        setBrand(b.canPickBrand ? 'Consolidated' : (b.brands[0] || 'Consolidated'));
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.error || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load BS when selectors change
  useEffect(() => {
    if (!period) return;
    let cancelled = false;
    setLoading(true); setErr(null); setDrill(null);
    const apiBrand = brand === 'Consolidated' ? 'all' : brand;
    fetchBalanceSheet(period, apiBrand, filters)
      .then(data => { if (!cancelled) { setBs(data); setLoading(false); } })
      .catch(e => { if (!cancelled) { setErr(e.response?.data?.error || e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [period, brand, filters]);

  const toggle = (key) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  // Open drill panel and load the per-entity data
  const openDrill = async ({ section, account }) => {
    setDrill({ section, account, data: null });
    try {
      const apiBrand = brand === 'Consolidated' ? 'all' : brand;
      const opts = {
        ...filters,
        reportCategory: section.reportCategory || undefined,
        glAccountKey:   account?.glAccountKey  || undefined,
      };
      const data = await fetchBSEntities(period, apiBrand, opts);
      setDrill({ section, account, data });
    } catch (e) {
      setDrill(d => ({ ...(d || {}), data: { error: e.response?.data?.error || e.message } }));
    }
  };

  const removeFilter = (k) => setFilters(f => { const out = { ...f }; delete out[k]; return out; });
  const clearFilters = () => setFilters({});

  const periodOptions = useMemo(() =>
    [...periods].sort((a, b) => (b.periodKey || '').localeCompare(a.periodKey || '')),
    [periods]
  );

  const assetSecs = useMemo(() => bs?.sections.filter(s => s.accountType === 'Asset') || [], [bs]);
  const liabSecs  = useMemo(() => bs?.sections.filter(s => s.accountType === 'Liability') || [], [bs]);
  const eqSecs    = useMemo(() => bs?.sections.filter(s => s.accountType === 'Equity') || [], [bs]);

  const totals = bs?.totals || {};
  const imbalanceAbs = Math.abs(totals.imbalance || 0);
  const reconciledPct = totals.assets ? (1 - imbalanceAbs / Math.abs(totals.assets)) * 100 : 0;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Balance Sheet</div>
          <div style={S.sub}>
            {brand === 'Consolidated' ? 'Consolidated · all brands' : `${brand} only`}
            {' · '}{period || '—'}
            {filters.country  && ` · ${filters.country}`}
            {filters.province && ` · ${filters.province}`}
            {filters.entityType && ` · ${filters.entityType}`}
          </div>
        </div>
        <div style={S.selectorBar}>
          {brands.canPickBrand && (
            <div>
              <div style={S.label}>Brand</div>
              <select style={S.select} value={brand} onChange={e => setBrand(e.target.value)}>
                {brands.brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={S.label}>Period</div>
            <select style={S.select} value={period || ''} onChange={e => setPeriod(e.target.value)}>
              {periodOptions.map(p => (
                <option key={p.periodKey} value={p.periodKey}>{p.periodKey}{p.monthName ? ` · ${p.monthName}` : ''}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {err && <div style={S.errorBox}>⚠ {err}</div>}
      {loading && <div style={S.loadingBox}>Loading…</div>}

      {!loading && bs && (
        <>
          {/* KPI strip */}
          <div style={S.kpiGrid}>
            <KpiTile label="Total Assets"      value={fmtMoney(totals.assets)}        sub={fmtFull(totals.assets)} />
            <KpiTile label="Total Liabilities" value={fmtMoney(totals.liabilities)}   sub={fmtFull(totals.liabilities)} />
            <KpiTile label="Total Equity"      value={fmtMoney(totals.equity)}        sub={fmtFull(totals.equity)} />
            <KpiTile label="Working Capital"   value={fmtMoney(totals.workingCapital)} sub={`Curr Assets − Curr Liab`}
                     valueColor={totals.workingCapital >= 0 ? 'var(--green-l)' : 'var(--red)'} />
            <KpiTile label="Current Ratio"     value={fmtRatio(totals.currentRatio)}  sub="< 1 · 1–1.5 · ≥ 1.5"
                     valueColor={ratioColor(totals.currentRatio)} />
            <KpiTile label="Debt : Equity"     value={fmtRatio(totals.debtEquity)}    sub="Liab ÷ Equity" />
          </div>

          {/* Active filter chips (set by clicking entity rows in drill panel) */}
          {Object.values(filters).some(Boolean) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12, fontSize: 12 }}>
              <div style={{ ...S.label, marginBottom: 0 }}>Filters</div>
              {Object.entries(filters).filter(([, v]) => v).map(([k, v]) => (
                <span key={k} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', background: 'rgba(200,149,32,0.10)',
                  border: '1px solid rgba(200,149,32,0.25)', borderRadius: 2,
                  fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--amber)',
                }}>
                  {k}: {v}
                  <button
                    onClick={() => removeFilter(k)}
                    style={{ background: 'transparent', border: 'none', color: 'var(--amber)', cursor: 'pointer', padding: 0, fontSize: 13, lineHeight: 1 }}
                  >×</button>
                </span>
              ))}
              <button onClick={clearFilters} style={{
                background: 'transparent', border: 'none', color: 'var(--page-muted)',
                cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10,
                letterSpacing: '0.1em', textTransform: 'uppercase',
              }}>Clear all</button>
            </div>
          )}

          {/* Two-tree layout */}
          <div style={S.twoCol}>
            <TreeColumn
              title="Assets"
              totalLabel="Total"
              totalValue={totals.assets}
              sections={assetSecs}
              accounts={bs.accounts}
              expanded={expanded}
              onToggle={toggle}
              onDrill={openDrill}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <TreeColumn
                title="Liabilities"
                totalLabel="Total"
                totalValue={totals.liabilities}
                sections={liabSecs}
                accounts={bs.accounts}
                expanded={expanded}
                onToggle={toggle}
                onDrill={openDrill}
              />
              <TreeColumn
                title="Equity"
                totalLabel="Total"
                totalValue={totals.equity}
                sections={eqSecs}
                accounts={bs.accounts}
                expanded={expanded}
                onToggle={toggle}
                onDrill={openDrill}
              />
            </div>
          </div>

          {/* Drill panel — entities contributing to the clicked section/GL */}
          {drill && (
            <div style={S.card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <div style={S.cardTitle}>
                    Entities — {drill.section.reportCategory}
                    {drill.account && ` · ${drill.account.glAccount}`}
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' }}>
                    {drill.account ? drill.account.glDescription : `${drill.section.accountType} section`}
                  </div>
                </div>
                <button style={S.closeBtn} onClick={() => setDrill(null)}>✕ Close</button>
              </div>
              {!drill.data ? (
                <div style={S.loadingBox}>Loading…</div>
              ) : drill.data.error ? (
                <div style={S.errorBox}>⚠ {drill.data.error}</div>
              ) : drill.data.entities.length === 0 ? (
                <div style={S.loadingBox}>No entities reported a balance for this line.</div>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Entity</th>
                      <th style={S.th}>Brand</th>
                      <th style={S.th}>Type</th>
                      <th style={S.th}>Country</th>
                      <th style={S.th}>Province</th>
                      <th style={{ ...S.th, ...S.thRight }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drill.data.entities.map(e => (
                      <tr key={e.entityKey}>
                        <td style={S.td}>{e.legalName}</td>
                        <td style={{ ...S.td, ...S.tdMono }}>{e.brand || '—'}</td>
                        <td style={{ ...S.td, ...S.tdMono }}>{e.entityType || '—'}</td>
                        <td style={{ ...S.td, ...S.tdMono }}>{e.country || '—'}</td>
                        <td style={{ ...S.td, ...S.tdMono }}>{e.province || '—'}</td>
                        <td style={{ ...S.td, ...S.tdRight }}>{fmtMoney(e.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Reconciliation footnote — surfaces parser imbalance */}
          <div style={S.footNote}>
            BS data parsed from Sage Evolution PDFs. Reconciliation: A − (L + E) ={' '}
            <span style={{ color: imbalanceAbs < 100_000 ? 'var(--green-l)' : 'var(--amber)', fontWeight: 600 }}>
              {fmtMoney(totals.imbalance)} ({reconciledPct.toFixed(2)}% of assets)
            </span>
            {imbalanceAbs >= 100_000 && ' — investigate outlier entities for missing sections'}.
          </div>
        </>
      )}
    </div>
  );
}

function KpiTile({ label, value, sub, valueColor }) {
  return (
    <div style={S.kpiCard}>
      <div style={S.kpiLabel}>{label}</div>
      <div style={{ ...S.kpiVal, ...(valueColor ? { color: valueColor } : {}) }}>{value}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}
